/**
 * i18n : catalogue FR partagé, accès par chemin pointé.
 * Aucun libellé métier en dur dans les composants — tout passe par t().
 *
 * RÈGLE ANTI-FUITE : une valeur venant de la base (état, catégorie, unité,
 * symptôme…) ne doit JAMAIS être rendue telle quelle. Elle passe par une des
 * fonctions `*Label()` ci-dessous, qui traduisent ce qu'elles connaissent et
 * humanisent le reste (`groupe_frigorifique` → « Groupe frigorifique »).
 * Le test `tests/unit/i18n-fuites.test.ts` interdit la récidive.
 */
import { fr, type MessageCatalog } from '@myequip/shared';

const catalog: MessageCatalog = fr;

/** t('interventions.types.depannage') → « Dépannage » ; interpolation {var}. */
export function t(path: string, vars?: Record<string, string | number>): string {
  const parts = path.split('.');
  let node: unknown = catalog;
  for (const part of parts) {
    if (node && typeof node === 'object' && part in (node as Record<string, unknown>)) {
      node = (node as Record<string, unknown>)[part];
    } else {
      return path; // clé manquante visible en dev, jamais de crash
    }
  }
  let text = typeof node === 'string' ? node : path;
  if (vars) {
    // Les libellés numériques historiques utilisent « (s) » dans le catalogue.
    // Résoudre l'accord ici garde toutes les vues cohérentes, y compris les badges.
    if ('n' in vars && text.includes('(s)')) {
      text = text.replaceAll('(s)', Number(vars.n) === 1 ? '' : 's');
    }
    for (const [key, value] of Object.entries(vars)) {
      text = text.replaceAll(`{${key}}`, String(value));
    }
  }
  return text;
}

/**
 * Humanisation de secours : une valeur inconnue du catalogue reste lisible
 * (`fusion_proposee` → « Fusion proposee ») au lieu de fuir en snake_case.
 */
function humanize(value: string): string {
  const words = value.replaceAll('_', ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** Traduit `prefix.value` ; humanise si la clé est absente. */
function labelFor(prefix: string, value: string | null | undefined): string {
  if (!value) return '—';
  const key = `${prefix}.${value}`;
  const label = t(key);
  return label === key ? humanize(value) : label;
}

/** Libellé d'un état métier (§13) : `etats.*`. */
export function stateLabel(state: string): string {
  return labelFor('etats', state);
}

/** Catégorie d'équipement (chambre_froide → « Chambre froide »). */
export function categoryLabel(category: string | null | undefined): string {
  return labelFor('equipements.categories', category);
}

/** Unité de stock (unite → « unité », kg, litre → « L »). */
export function unitLabel(unit: string | null | undefined): string {
  return labelFor('unites', unit);
}

/** Symptôme d'une demande client (temperature_haute → « Température trop haute »). */
export function symptomLabel(code: string | null | undefined): string {
  return labelFor('symptomes', code);
}

/** Catégorie d'article (piece → « Pièce détachée »). */
export function articleCategoryLabel(category: string | null | undefined): string {
  return labelFor('articles.categories', category);
}

/** Type d'emplacement de stock (vehicule → « Véhicule »). */
export function locationKindLabel(kind: string | null | undefined): string {
  return labelFor('emplacements', kind);
}

/** Prestation couverte par un contrat (main_oeuvre → « Main-d'œuvre »). */
export function coverageItemLabel(item: string | null | undefined): string {
  return labelFor('contrats.prestations', item);
}

/** Mode de couverture (plafonne → « Plafonné »). */
export function coverageModeLabel(mode: string | null | undefined): string {
  return labelFor('contrats.modes', mode);
}

/** Rôle d'un contact client (facturation → « Facturation »). */
export function contactRoleLabel(role: string | null | undefined): string {
  return labelFor('contacts.roles', role);
}

/** Rôle affiché d'un membre : le nom de son rôle dans l'entreprise, sinon son rôle de base. */
export function roleDisplayName(user: { role: string; company_role_name?: string | null }): string {
  return user.company_role_name ?? roleLabel(user.role);
}

/** Rôle de base d'un utilisateur interne (technicien → « Technicien »). */
export function roleLabel(role: string | null | undefined): string {
  return labelFor('roles', role);
}

/** Type d'événement de planning (conge → « Congé / RTT / maladie »). */
export function scheduleKindLabel(kind: string | null | undefined): string {
  return labelFor('planning.evenements', kind);
}

/** Type d'intervention (depannage → « Dépannage »). */
export function interventionTypeLabel(type: string | null | undefined): string {
  return labelFor('interventions.types', type);
}

/** Mode de règlement (virement → « Virement »). */
export function paymentMethodLabel(method: string | null | undefined): string {
  return labelFor('factures.moyens', method);
}

/** Nature d'une ligne de facture dans les analyses (main_oeuvre → « Main-d'œuvre »). */
export function invoiceLineKindLabel(kind: string | null | undefined): string {
  return labelFor('analyses.lignes', kind);
}

/** Ancienneté d'un reste à encaisser (j1_30 → « En retard de 1 à 30 jours »). */
export function agingLabel(age: string | null | undefined): string {
  return labelFor('analyses.anciennete', age);
}

/** Catégorie de temps (trajet → « Trajet »). */
export function timeCategoryLabel(category: string | null | undefined): string {
  return labelFor('temps.categories', category);
}
